import { useState } from "react";
import { MapPin, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

// Same rules the backend applies — kept intentionally simple so we catch the
// obvious "typed a name / random string" case without over-engineering.
// Anything that passes here still gets a full server-side validation.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_LOOSE_RE = /^\+?\d[\d\s().-]{7,20}$/;

function looksLikeContact(raw) {
  const v = (raw || "").trim();
  if (!v) return false;
  if (EMAIL_RE.test(v)) return true;
  if (!PHONE_LOOSE_RE.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
}

/**
 * Shown on the Marketplace when the user's search area is outside every
 * carrier's coverage circle. Captures phone or email so ops know exactly
 * where the demand is coming from.
 */
export default function WaitingListCallout({ areaLabel, lat, lng, testId = "waiting-list-callout" }) {
  const [contact, setContact] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const trimmed = contact.trim();
  const isValid = looksLikeContact(trimmed);
  // Only show the inline hint AFTER the user has typed enough characters to
  // reasonably be attempting a real contact — avoids yelling "invalid" at the
  // very first keystroke.
  const showHint = trimmed.length >= 4 && !isValid;

  const submit = async (e) => {
    e?.preventDefault();
    if (!isValid) {
      toast.error("Please enter a valid phone number or email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/recruitment-leads", {
        contact: trimmed,
        area_label: areaLabel || "Unknown area",
        lat: lat ?? null,
        lng: lng ?? null,
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch when we launch nearby");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testId}-success`}
        className="relative overflow-hidden rounded-2xl border border-[#4A6B56]/30 bg-gradient-to-br from-[#1B3624] to-[#274d33] text-white p-6 sm:p-8"
      >
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#D67B54]/25 blur-2xl" aria-hidden />
        <div className="relative flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#D67B54] flex items-center justify-center shrink-0">
            <BellRing className="w-5 h-5" />
          </div>
          <div>
            <div className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>
              You&apos;re on the list
            </div>
            <p className="text-sm text-white/80 mt-1.5 leading-relaxed">
              We&apos;ll drop you a message the moment a licensed carrier goes live near <span className="font-medium text-white">{areaLabel || "your area"}</span>. In the meantime, feel free to browse posts in other regions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className="relative overflow-hidden rounded-2xl border border-[#4A6B56]/30 bg-gradient-to-br from-[#1B3624] to-[#274d33] text-white p-6 sm:p-8"
    >
      {/* Terracotta glow — pulls the eye without shouting */}
      <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[#D67B54]/25 blur-3xl" aria-hidden />
      <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-[#4A6B56]/25 blur-3xl" aria-hidden />

      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#D67B54] flex items-center justify-center shrink-0 shadow-lg shadow-[#D67B54]/30">
          <MapPin className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.14em] text-[#D67B54] font-medium">
            Not in your area yet
          </div>
          <h3 className="mt-2 text-xl sm:text-2xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>
            We haven&apos;t reached this area yet!
          </h3>
          <p className="mt-2 text-sm text-white/80 leading-relaxed max-w-lg">
            We&apos;re currently onboarding licensed carriers in <span className="text-white font-medium">{areaLabel || "your region"}</span>. Want us to text you when we launch nearby?
          </p>

          <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2 max-w-md">
            <input
              data-testid="waiting-list-input"
              type="text"
              inputMode="text"
              autoComplete="tel-national email"
              placeholder="Phone number or email"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              className={`flex-1 min-w-0 rounded-full bg-white/95 text-[#1B3624] placeholder-[#6B6965] px-5 py-3 text-sm outline-none focus:ring-2 focus:bg-white transition ${
                showHint ? "focus:ring-[#F5A971] ring-2 ring-[#F5A971]/60" : "focus:ring-[#D67B54]"
              }`}
              disabled={submitting}
              aria-invalid={showHint}
              aria-describedby={showHint ? `${testId}-hint` : undefined}
            />
            <button
              data-testid="waiting-list-submit"
              type="submit"
              disabled={submitting || !isValid}
              className="rounded-full bg-[#D67B54] hover:bg-[#c26b45] active:bg-[#b25f3d] text-white font-medium px-6 py-3 text-sm inline-flex items-center justify-center gap-2 transition shadow-lg shadow-[#D67B54]/25 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <BellRing className="w-4 h-4" />
              {submitting ? "Sending…" : "Notify Me"}
            </button>
          </form>
          {showHint ? (
            <p
              id={`${testId}-hint`}
              data-testid="waiting-list-hint"
              className="mt-2 text-[11px] text-[#F5A971]"
            >
              Doesn&apos;t look like a valid phone or email — try again
            </p>
          ) : (
            <p className="mt-3 text-[11px] text-white/50">
              One message per area. No marketing, no third-party sharing.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
